<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { usePresentLevelsStore } from './presentlevels.store'
import { computed, ComputedRef, onMounted, ref } from "vue";
import AddSubjectModal from './PresentlevelsAddSubjectModal.vue';
import PresentlevelsSubjectSummary from "./PresentlevelsSubjectSummary.vue";
import SubjectSummaryLoading from './SubjectSummaryLoading.vue'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin'
import { EVENT_NAMES } from '@/tracking/analytics/event_names'
import PresentlevelsStudentHeader from './PresentlevelsStudentHeader.vue'
import PresentlevelsAlignedGoalsPanel from './PresentlevelsAlignedGoalsPanel.vue'
import ComposeIODButton from './ComposeIODButton.vue'

const presentLevelsStore = usePresentLevelsStore()

const { plSession, enrolledGradeLabel, subjectSummaries, loadingSubjectSummaries } = storeToRefs(presentLevelsStore)

const studentName: ComputedRef<string> = computed(() => plSession.value?.student_name || '')

// Modal state
const showAddSubjectModal = ref(false)

// Track collapsed state for each summary
const collapsedSummaries = ref<Record<string, boolean>>({})

const handleSummaryCollapsed = (summaryUuid: string, isCollapsed: boolean) => {
  collapsedSummaries.value[summaryUuid] = isCollapsed
}

// Handler for adding a new subject
const handleAddSubject = async (subjectUUIDs: string[], subjectLabel: string) => {
  if (!plSession.value) {
    console.error('No session available to add subjects to')
    return
  }

  try {
    await presentLevelsStore.createSubjectSummaries({
      studentName: plSession.value.student_name,
      gradeUUID: plSession.value.enrolled_grade_uuid,
      subjectUUIDs: subjectUUIDs,
      sessionUUID: plSession.value.uuid
    })

    // Fetch categories for the newly created subject summaries
    await presentLevelsStore.fetchCategoriesForSummarySubjects()

    analyticsTracker.sendEvent(
      EVENT_NAMES.PRESENTLEVELS.SUMMARY.ADDED_AREA,
      { plw_added_subject: subjectLabel }
    )
  } catch (error) {
    console.error('Failed to add subjects:', error)
  }
}

const props = defineProps({
  session_uuid: {
    type: String,
    default: ''
  }
})

const sessionSubjects = computed(() => {
  return subjectSummaries.value?.map(s => s.subject_label)
})

const analyticsTracker = getAnalyticsTracker().getChildContext({
  plw_session_enrolled_grade: enrolledGradeLabel,
  plw_session_subjects: sessionSubjects,
})

function handleClickAddArea() {
  analyticsTracker.sendEvent(EVENT_NAMES.PRESENTLEVELS.SUMMARY.CLICKED_ADD_AREA)
  showAddSubjectModal.value = true
}

async function handleCopySummary() {
  await presentLevelsStore.fetchSessionDetails(props.session_uuid)
}

onMounted(async () => {
  const promises = [presentLevelsStore.fetchSessionDetails(props.session_uuid)]
  if (!presentLevelsStore.levelOptions.length) {
    promises.push(presentLevelsStore.fetchLevelOptions())
  }
  if (!presentLevelsStore.subjectOptions.length) {
    promises.push(presentLevelsStore.fetchSubjectOptions())
  }
  await Promise.all(promises)
})
</script>

<template>
<main>
  <div class="flex justify-center">
    <div class="relative">
      <div class="flex items-start">
        <PresentlevelsStudentHeader
        :student-name="studentName"
        :grade-label="enrolledGradeLabel"
        />
      </div>
      <SubjectSummaryLoading v-if="loadingSubjectSummaries" />
      <div
        v-for="summary in subjectSummaries"
        :key="`subject-${summary.uuid}`"
        class="flex flex-col lg:flex-row items-start h-fit mb-5"
      >
        <PresentlevelsSubjectSummary
          :summary="summary"
          :category-options="presentLevelsStore.getCategoriesForSubject(summary.subject_uuid)"
          @copied="handleCopySummary"
          @collapsed="(isCollapsed) => handleSummaryCollapsed(summary.uuid, isCollapsed)"
        />
        <PresentlevelsAlignedGoalsPanel
          :summaryUUID="summary.uuid"
          :parent-collapsed="collapsedSummaries[summary.uuid] || false"
        />
      </div>
      <div v-if="!loadingSubjectSummaries" class="mt-8 hover:font-bold">
      <button
        @click="handleClickAddArea"
        class="w-full py-6 border border-dashed border-gray-700 rounded-lg bg-blue-200 transition-colors duration-200 flex items-center justify-center gap-1 text-gray-900 md:w-plw-subject-summary"
      >
          <span class="material-icons-round text-xl" aria-hidden="true">add</span>
          <span class="text-lg">Add Present Level Area</span>
        </button>
      </div>
    </div>
  </div>
  <AddSubjectModal
    v-model:visible="showAddSubjectModal"
    @add-subject="handleAddSubject"
  />
</main>
</template>

<style scoped>

</style>